import React, { useEffect, useRef, useState } from "react";
import { Button, Space, Table, Input, Tooltip, DatePicker, message, ConfigProvider } from "antd";
import type { TableColumnsType } from "antd";
import { ExportOutlined, EditOutlined, CarOutlined, RedoOutlined } from "@ant-design/icons";
import AssignModel from "../../components/Assignmodel";
import { getLocationsRequest } from "../../redux/actions/bookingActions";
import "../../style/Live.css";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { getCancelByDateRequest, resetUpdateToNewBooking, updateToNewBookingRequest } from "../../redux/actions/cancelActions";
import { buildDatePayload } from "../../utils/dateUtils";
import { useBranchId } from "../../utils/hooks/useBranch";
import { useMountEffectOnce } from "../../utils/hooks/useMountEffectOnce";
import { useListAutoRefresh, BOOKING_LIST_EVENTS } from "../../utils/hooks/useListAutoRefresh";
import { noRowSelectionBgTheme } from "../../utils/tableTheme";
import { assignTripRequest } from "../../redux/actions/newAction";
import { exportToExcel, ExcelColumn } from "../../utils/excelExport";
import { COPY_CELL } from "../../utils/copyGuard";

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  [key: string]: any;
}

const { RangePicker } = DatePicker;
const { Search } = Input;

const pageWrapper: React.CSSProperties = {
  padding: 0,
  background: "linear-gradient(135deg,#f7f2fb,#fbf7ff)",
  minHeight: "clamp(520px, 78vh, 900px)",
  display: "flex",
  flexDirection: "column",
};

const columns: TableColumnsType<DataType> = [
  {
    title: "Book ID",
    dataIndex: "id",
    key: "id",
    className: COPY_CELL,
    align: "center",
    width: 120,
    fixed: "left",
    render: (text) => <strong style={{ color: "#89339c" }}>{text}</strong>,
  },
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    className: COPY_CELL,
    align: "center",
    width: "auto",
    fixed: "left",
  },
  {
    title: "Mobile",
    dataIndex: "mobileno",
    key: "mobileno",
    className: COPY_CELL,
    align: "center",
    width: 120,
    fixed: "left",
  },
  {
    title: "Date",
    dataIndex: "bookedtime",
    key: "bookedtime",
    align: "center",
    width: 110,
    render: (text) => text ? dayjs(text).format("DD/MM/YYYY") : "-",
  },
  {
    title: "Time",
    dataIndex: "bookedtime",
    key: "time",
    align: "center",
    width: 120,
    render: (text) => text ? dayjs(text).format("hh:mm A") : "-",
  },
  {
    title: "From Location",
    dataIndex: "fromloc",
    key: "fromloc",
    align: "center",
    width: 150,
    ellipsis: true,
    render: (text: string) => (
      <Tooltip title={text} placement="topLeft">
        <span>{text || "-"}</span>
      </Tooltip>
    ),
  },
  {
    title: "To Location",
    dataIndex: "toloc",
    key: "toloc",
    align: "center",
    width: 150,
    ellipsis: true,
    render: (text: string) => (
      <Tooltip title={text} placement="topLeft">
        <span>{text || "-"}</span>
      </Tooltip>
    ),
  },
  {
    title: "Reason",
    dataIndex: "reason",
    key: "reason",
    align: "center",
    width: "auto",
  },
  {
    title: "Vehicle",
    dataIndex: "vehicletype",
    key: "vehicletype",
    align: "center",
    width: 120,
  },
  {
    title: "Fare Type",
    dataIndex: "faretype",
    key: "faretype",
    align: "center",
    width: "auto",
  },
  {
    title: "Credit",
    dataIndex: "iscredit",
    key: "iscredit",
    align: "center",
    width: 130,
    render: (val) => val ? "Yes" : "No",
  },
  {
    title: "Goods",
    dataIndex: "goods",
    key: "goods",
    align: "center",
    width: "auto",
    render: (val) => val ?? "-",
  },
  {
    title: "Booked By",
    dataIndex: "bookedby",
    key: "bookedby",
    align: "center",
    width: "auto",
  },
  {
    title: "Cab ID",
    dataIndex: "cabid",
    key: "cabid",
    align: "center",
    width: 110,
  },
  {
    title: "City",
    dataIndex: "city",
    key: "city",
    align: "center",
    width: "auto",
  },
];

const Cancel: React.FC = () => {
  const dispatch = useDispatch();
  const branchId = useBranchId();
  const auth = useSelector((state: any) => state.auth);
  const user = auth?.user;
  const { assignData, assignTripLoading, error } = useSelector((state: any) => state.newBooking || {});

  const { cancelledBookings = [], cancelLoading } = useSelector(
    (state: any) => state.cancel
  );

  const { loading: updateLoading, success: updateSuccess, error: updateError } = useSelector(
    (state: any) => state.cancel
  );

  const { locations } = useSelector((state: any) => state.booking || {});

  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const [selectedBookingData, setSelectedBookingData] = useState<DataType | null>(null);

  const [searchText, setSearchText] = useState<string>("");
  const tableClass = "Live-table";

  const today = dayjs();

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [assignOpen, setAssignOpen] = useState(false);

  const lastFetchedRangeRef = useRef<string | null>(null);

  const fetchCancelledBookings = () => {
    const { fromDate: start, toDate: end } = buildDatePayload(
      fromDate,
      toDate.add(1, "day")
    );

    dispatch(
      getCancelByDateRequest(
        branchId,
        start,
        end
      )
    );
  };

  useListAutoRefresh(BOOKING_LIST_EVENTS, fetchCancelledBookings);

  useEffect(() => {
    if (updateSuccess) {
      message.success("Booking updated to new booking!");
      dispatch(resetUpdateToNewBooking());
      setSelectedRowKey(null);
      setSelectedBookingData(null);
      fetchCancelledBookings();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (!fromDate || !toDate) return;
    const rangeKey = `${branchId}|${fromDate.valueOf()}|${toDate.valueOf()}`;
    if (lastFetchedRangeRef.current === rangeKey) return;
    lastFetchedRangeRef.current = rangeKey;
    fetchCancelledBookings();
  }, [dispatch, branchId, fromDate, toDate]);

  useMountEffectOnce(() => {
    if (!locations?.length) dispatch(getLocationsRequest(branchId));
  });

  const handleRefresh = () => {
    setSearchText("");
    setSelectedRowKey(null);
    setSelectedBookingData(null);
    fetchCancelledBookings();
  };

  useEffect(() => {
    if (assignData) {
      message.success("Trip assigned successfully!");
      setAssignOpen(false);
      setSelectedRowKey(null);
      setSelectedBookingData(null);
      dispatch({ type: "ASSIGN_TRIP_RESET" });
      fetchCancelledBookings();
    }
  }, [assignData]);

  useEffect(() => {
    if (error) message.error(error);
  }, [error]);

  useEffect(() => {
    if (updateError) message.error(updateError);
  }, [updateError]);

  const filteredBookings = cancelledBookings.filter((booking: DataType) => {

    if (!searchText) return true;

    const lower = searchText.toLowerCase();

    return (
      booking.id?.toString().toLowerCase().includes(lower) ||
      booking.name?.toLowerCase().includes(lower) ||
      booking.mobileno?.toString().toLowerCase().includes(lower)
    );
  });

  const handleBooktonewpage = () => {
    if (!selectedRowKey) return;
    dispatch(updateToNewBookingRequest({
      bookid: Number(selectedRowKey),
      updatedBy: user?.username || localStorage.getItem("username") || "Admin",
    }));
  };

  const rowSelection = {
    type: "radio" as const,
    selectedRowKeys: selectedRowKey
      ? [selectedRowKey]
      : [],
    onSelect: (record: DataType) => {
      const key = String(record.id);
      if (selectedRowKey === key) {
        setSelectedRowKey(null);
        setSelectedBookingData(null);
      } else {
        setSelectedRowKey(key);
        setSelectedBookingData(record);
      }
    },
    onChange: (
      keys: React.Key[],
      selectedRows: DataType[]
    ) => {
      const key =
        keys[0]
          ? String(keys[0])
          : null;
      setSelectedRowKey(key);
      setSelectedBookingData(
        selectedRows[0] || null
      );

    },
  };

  const handleExport = () => {
    const columnsToExport: ExcelColumn[] = [
      { header: "Book ID", key: "id" },
      { header: "Name", key: "name" },
      { header: "Mobile", key: "mobileno" },
      {
        header: "Date",
        formatter: (row) => (row.bookedtime ? dayjs(row.bookedtime).format("DD-MM-YYYY") : "-"),
      },
      {
        header: "Time",
        formatter: (row) => (row.bookedtime ? dayjs(row.bookedtime).format("hh:mm A") : "-"),
      },
      { header: "From Location", key: "fromloc" },
      { header: "To Location", key: "toloc" },
      { header: "Reason", key: "reason" },
      { header: "Vehicle", key: "vehicletype" },
      { header: "Fare Type", key: "faretype" },
      {
        header: "Credit",
        formatter: (row) => (row.iscredit ? "Yes" : "No"),
      },
      {
        header: "Goods",
        formatter: (row) => row.goods ?? "-",
      },
      { header: "Booked By", key: "bookedby" },
      { header: "Cab ID", key: "cabid" },
      { header: "City", key: "city" },
    ];

    exportToExcel(filteredBookings, columnsToExport, "Canceled_Bookings");
  };

  return (
    <div style={pageWrapper}>
      <div className="toolbar-glass">

        <Space size={[40, 16]} wrap>
          {/* <div className="action-btn-group">
            <AssignModel
              open={assignOpen}
              setOpen={setAssignOpen}
              selectedRowKey={selectedRowKey}
              selectedBookingData={selectedBookingData ? {
                ...selectedBookingData,
                id: String(selectedBookingData.id),
                fromloc: selectedBookingData.fromloc ?? selectedBookingData.fromlocation ?? selectedBookingData.pickuplocation,
                toloc: selectedBookingData.toloc ?? selectedBookingData.tolocation,
                remarks: selectedBookingData.remarks,
                vehicletypeid: selectedBookingData.vehicletypeid ?? selectedBookingData.vehicletype,
                branchid: selectedBookingData.cityid ?? branchId,
                fromlat: selectedBookingData.fromloclat ?? selectedBookingData.fromlat ?? selectedBookingData.pickuplat ?? selectedBookingData.pickup_lat ?? selectedBookingData.pickupLat ?? selectedBookingData.pickupLatitude ?? selectedBookingData.lat ?? selectedBookingData.latitude,
                fromlong: selectedBookingData.fromloclong ?? selectedBookingData.fromlong ?? selectedBookingData.pickuplong ?? selectedBookingData.pickup_long ?? selectedBookingData.pickupLong ?? selectedBookingData.pickupLongitude ?? selectedBookingData.long ?? selectedBookingData.longitude,
              } : null}
              assignLoading={assignTripLoading}
              onAssignConfirm={(rowKey, vehicleData) => {
                if (!vehicleData) {
                  message.warning("Please select a vehicle");
                  return;
                }
                dispatch(assignTripRequest({
                  cabid: vehicleData.cabId,
                  driverid: vehicleData.driverid,
                  bookid: rowKey,
                  assignedby: user?.username || localStorage.getItem("username") || "Admin",
                  emptyKM: vehicleData.distance,
                }));
              }}
            />

            <Button className="action-btn secondary"
              disabled={!selectedRowKey || updateLoading}
              onClick={handleBooktonewpage}>
              {updateLoading ? "Updating..." : "Book Now"}
            </Button>

          </div> */}
          <Tooltip title="Refresh">
            <Button
              className="btn-export"
              onClick={handleRefresh}
            >
              <RedoOutlined />
            </Button>
          </Tooltip>
        </Space>

        <Space size="large" wrap>
          <div
            className="active-pill"
            style={{ fontSize: "18px" }}
          >
            <CarOutlined />
            Trips : {filteredBookings.length}
          </div>

          <Search
            className="custom-search-complete"
            placeholder="Search bookings..."
            enterButton
            style={{ width: "100%", maxWidth: 250, minWidth: 170 }}
            size="large"
            value={searchText}
            onChange={(e) =>
              setSearchText(e.target.value)
            }
            onSearch={(value) =>
              setSearchText(value)
            }
            allowClear
            onClear={() =>
              setSearchText("")
            }
          />

          {/* <Tooltip title="Export">
            <Button className="btn-export" onClick={handleExport}>
              <ExportOutlined />
            </Button>
          </Tooltip> */}
        </Space>
      </div>

      <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>

        <ConfigProvider theme={noRowSelectionBgTheme}>
          <Table<DataType>
            bordered
            loading={cancelLoading}
            className={tableClass}
            rowSelection={rowSelection}
            columns={columns}
            dataSource={
              filteredBookings.map(
                (item: any, index: number) => ({
                  ...item,
                  key: String(
                    item.id || index
                  )
                })
              )
            }
            rowKey="key"
            scroll={{
              x: 2600,
              y: "calc(100vh - 340px)"
            }}
            pagination={{
              pageSizeOptions: ["5", "10", "100"],
              showSizeChanger: true,
              defaultPageSize: 10,
            }}
          />
        </ConfigProvider>
      </div>
    </div>
  );
};
export default Cancel;
